import React, { createContext, useState, useContext, useEffect } from 'react';
import { setSentryUser, clearSentryUser } from '../instrumentation';

const AuthContext = createContext(null);

// Use relative URL for same-origin deployment, or env variable for cross-origin dev
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Helper to build fetch options with both cookie and Authorization header
const getAuthFetchOptions = (extra = {}) => {
  const token = localStorage.getItem('firstcut_session_token');
  const headers = extra.headers || {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return { ...extra, credentials: 'include', headers };
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasAcceptedConsent, setHasAcceptedConsent] = useState(true); // Default true to avoid flash

  // Set Sentry user context whenever user changes
  useEffect(() => {
    if (user) {
      setSentryUser(user);
    } else {
      clearSentryUser();
    }
  }, [user]);

  useEffect(() => {
    // Don't check auth if we're in the middle of an auth callback
    // The AuthCallback component will handle setting the user
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      // Auth callback will handle this - just set loading to false
      setLoading(false);
      return;
    }
    
    // Check if we just authenticated (from AuthCallback)
    const justAuthenticated = sessionStorage.getItem('just_authenticated');
    const loginTime = sessionStorage.getItem('login_time');
    const timeSinceLogin = loginTime ? (Date.now() - parseInt(loginTime)) : Infinity;
    
    // If we just authenticated (within last 30 seconds), trust localStorage
    if (justAuthenticated || timeSinceLogin < 30000) {
      const cachedUser = localStorage.getItem('firstcut_user');
      if (cachedUser) {
        try {
          const userData = JSON.parse(cachedUser);
          setUser(userData);
          // Set consent status from cache
          setHasAcceptedConsent(userData.has_accepted_consent || false);
          setLoading(false);
          
          // Clear the just_authenticated flag after 10 seconds
          setTimeout(() => {
            sessionStorage.removeItem('just_authenticated');
          }, 10000);
          
          // Verify with backend in background after a delay
          setTimeout(() => {
            verifyAuthInBackground();
          }, 2000);
          
          return;
        } catch (e) {
          localStorage.removeItem('firstcut_user');
        }
      }
    }
    
    // Check localStorage first for faster initial load
    const cachedUser = localStorage.getItem('firstcut_user');
    if (cachedUser) {
      try {
        const userData = JSON.parse(cachedUser);
        setUser(userData);
        // Also set consent status from cache to prevent flash
        setHasAcceptedConsent(userData.has_accepted_consent || false);
      } catch (e) {
        localStorage.removeItem('firstcut_user');
      }
    }
    
    // Then verify with backend (will update if session is invalid)
    checkAuth();
  }, []);

  const verifyAuthInBackground = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/me`, getAuthFetchOptions());
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setHasAcceptedConsent(userData.has_accepted_consent || false);
        localStorage.setItem('firstcut_user', JSON.stringify(userData));
      }
      // Don't clear user if verification fails - we just authenticated
    } catch (error) {
      console.error('Background auth verification failed:', error);
    }
  };

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/me`, getAuthFetchOptions());
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        localStorage.setItem('firstcut_user', JSON.stringify(userData));
        // Check consent status
        setHasAcceptedConsent(userData.has_accepted_consent || false);
      } else {
        // Session invalid, clear everything
        setUser(null);
        localStorage.removeItem('firstcut_user');
        localStorage.removeItem('firstcut_session');
        localStorage.removeItem('firstcut_session_token');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const acceptConsent = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/accept-consent`, getAuthFetchOptions({ method: 'POST' }));
      if (response.ok) {
        setHasAcceptedConsent(true);
        // Update cached user
        if (user) {
          const updatedUser = { ...user, has_accepted_consent: true };
          setUser(updatedUser);
          localStorage.setItem('firstcut_user', JSON.stringify(updatedUser));
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Accept consent failed:', error);
      return false;
    }
  };

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await fetch(`${API_BASE}/api/auth/logout`, getAuthFetchOptions({ method: 'POST' }));
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setUser(null);
      localStorage.removeItem('firstcut_user');
      localStorage.removeItem('firstcut_session');
      localStorage.removeItem('firstcut_session_token');
      window.location.href = '/';
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setUser, checkAuth, hasAcceptedConsent, acceptConsent }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
